import Tab from "@mui/material/Tab";
import Tabs, { tabsClasses } from "@mui/material/Tabs";
import { recordPanelId, recordTabId, type RecordTabDef } from "@/base/tabParam";

// A record page's tab row, shared by BaseForm and the pages that draw their own
// header around it (the Surety account profile, BRK-S398). One strip, so the
// two cannot drift in how a tab scrolls on a phone or where it keeps its state
// (the state is `useTabParam`, src/base/tabParam.ts).

/**
 * The strip itself. It scrolls inside its own box rather than wrapping, so
 * seven tabs at 375px stay one row and never push the PAGE sideways.
 */
export function RecordTabs({
  tabs,
  active,
  onSelect,
  idBase,
  ariaLabel,
  badges,
}: {
  tabs: readonly RecordTabDef[];
  active: string;
  onSelect: (id: string) => void;
  idBase: string;
  ariaLabel: string;
  /**
   * A count beside a tab's label, by tab id — what is waiting on the broker
   * there (the submission's required documents, BRK-S424). Shown above zero.
   * Hidden from the accessible name, which stays the tab's label: the panel
   * itself says what the count is.
   */
  badges?: Partial<Record<string, number>>;
}) {
  return (
    <Tabs
      value={active}
      onChange={(_, v: string) => onSelect(v)}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
      aria-label={ariaLabel}
      sx={{
        flex: 1,
        minWidth: 0,
        // MUI keeps a disabled scroll button as an invisible 40px gutter;
        // collapse it so the first tab sits flush with the panel below until
        // there is somewhere to scroll back to.
        [`& .${tabsClasses.scrollButtons}.Mui-disabled`]: { width: 0 },
      }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.id}
          value={t.id}
          label={
            (badges?.[t.id] ?? 0) > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                {t.label}
                <span
                  aria-hidden="true"
                  data-testid={`tab-badge-${t.id}`}
                  className="inline-flex min-w-5 items-center justify-center rounded-full bg-brk-accent px-1.5 text-xs font-semibold leading-5 text-white"
                >
                  {badges?.[t.id]}
                </span>
              </span>
            ) : (
              t.label
            )
          }
          id={recordTabId(idBase, t.id)}
          aria-controls={recordPanelId(idBase, t.id)}
        />
      ))}
    </Tabs>
  );
}
